import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import { Button, DropdownSearch, getOverlayPortalParent, PopoverShell, TagColored, Text } from '@spark';
import { getSparkTagVariant } from '@/features/datasets/components/DataSelection/sparkLabelColors';

const MAX_VISIBLE_ENTITY_TYPES = 6;

/**
 * Spark-only entity-type picker anchored above a highlighted word or span.
 *
 * When `entityActions` is provided (annotate-modal usage) the menu also shows
 * Validate/Unvalidate and Remove-tag actions for the hovered entity; without
 * it the menu behaves exactly as in the data-collection flows.
 */
const NerEntityTypeMenu = ({
    open,
    anchorRect,
    entityNames,
    selectedType,
    onSelect,
    onClose,
    anchorEl,
    entityActions = null,
}) => {
    const menuRef = useRef(null);
    const names = entityNames || [];
    const showSearch = names.length > MAX_VISIBLE_ENTITY_TYPES;

    useEffect(() => {
        if (!open) return undefined;
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') onClose();
        };
        const handleMouseDown = (event) => {
            if (menuRef.current?.contains(event.target)) return;
            onClose();
        };
        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('mousedown', handleMouseDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('mousedown', handleMouseDown);
        };
    }, [open, onClose]);

    const items = useMemo(
        () =>
            names.map((name) => ({
                label: <TagColored label={name} variant={getSparkTagVariant(name, names)} />,
                searchText: name,
                state: selectedType === name ? 'selected' : undefined,
                onClick: () => onSelect(name),
            })),
        [names, selectedType, onSelect]
    );

    if (!open || !anchorRect) return null;

    const top = anchorRect.top;
    const left = (anchorRect.left || 0) + (anchorRect.width || 0) / 2;

    return createPortal(
        <div
            ref={menuRef}
            className="gdai-ner-entity-menu-anchor"
            style={{
                '--gdai-ner-menu-top': `${top}px`,
                '--gdai-ner-menu-left': `${left}px`,
            }}
        >
            <PopoverShell tip tipDirection="bottom">
                <Text variant="detail-medium" as="div" className="fs-unmask">
                    Select Entity Type
                </Text>
                <DropdownSearch
                    embedded
                    fill
                    showFooter={false}
                    showPrefixIcon={false}
                    showInfoIcon={false}
                    showSearch={showSearch}
                    maxVisibleItems={MAX_VISIBLE_ENTITY_TYPES}
                    autoFocus={showSearch}
                    searchPlaceholder="Search"
                    items={items}
                />
                {entityActions ? (
                    <div className="gdai-ner-entity-menu-actions">
                        {entityActions.onToggleValidated ? (
                            <Button
                                size="tiny"
                                variant="ghost"
                                onClick={entityActions.onToggleValidated}
                            >
                                {entityActions.validated ? 'Unvalidate' : 'Validate'}
                            </Button>
                        ) : null}
                        {entityActions.onRemove ? (
                            <Button size="tiny" variant="ghost" onClick={entityActions.onRemove}>
                                Remove tag
                            </Button>
                        ) : null}
                    </div>
                ) : null}
            </PopoverShell>
        </div>,
        getOverlayPortalParent(anchorEl || null)
    );
};

NerEntityTypeMenu.propTypes = {
    open: PropTypes.bool,
    anchorRect: PropTypes.shape({
        top: PropTypes.number,
        left: PropTypes.number,
        width: PropTypes.number,
        height: PropTypes.number,
    }),
    entityNames: PropTypes.arrayOf(PropTypes.string),
    selectedType: PropTypes.string,
    onSelect: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    anchorEl: PropTypes.object,
    entityActions: PropTypes.shape({
        validated: PropTypes.bool,
        onToggleValidated: PropTypes.func,
        onRemove: PropTypes.func,
    }),
};

export default NerEntityTypeMenu;
